import React, {useState, useMemo} from 'react'
import {useNavigate} from 'react-router-dom'
import {Servicio} from '../core/models'
import '../../socios/socios.scss'

interface Props {
  servicios: Servicio[]
}

export const ServiciosTable: React.FC<Props> = ({servicios}) => {
  const navigate = useNavigate()
  
  // Para filtrar
  const [filter, setFilter] = useState('')
  // Para paginar (paginación local deshabilitada si viene del backend, pero mantenemos la lógica por si acaso)
  // En este caso, como servicios ya viene paginado desde ServiciosPage, mostramos todo.
  
  // Índice de la fila que está expandida (o null si ninguna)
  const [expandedRow, setExpandedRow] = useState<number | null>(null)

  // 1) Filtrado
  const filteredData = useMemo(() => {
    if (!filter) return servicios

    const lowerFilter = filter.toLowerCase()

    return servicios.filter((servicio) => {
      const nombre = servicio.nombre.toLowerCase()
      const descripcion = servicio.descripcion ? servicio.descripcion.toLowerCase() : ''
      const valorStr = servicio.valor.toString().toLowerCase()

      return (
        nombre.includes(lowerFilter) ||
        descripcion.includes(lowerFilter) ||
        valorStr.includes(lowerFilter)
      )
    })
  }, [filter, servicios])

  // 3) Expansión de fila
  const toggleExpandRow = (index: number) => {
    if (expandedRow === index) {
      setExpandedRow(null) // cerrar si hacemos clic de nuevo
    } else {
      setExpandedRow(index)
    }
  }

  return (
    <>
      <div className='card-header border-0 pt-6'>
        <div className='card-title'>
          <div className='d-flex align-items-center position-relative my-1'>
            <i className='ki-duotone ki-magnifier fs-1 position-absolute ms-6'>
              <span className='path1'></span>
              <span className='path2'></span>
            </i>
            <input
              type='text'
              data-kt-user-table-filter='search'
              className='form-control form-control-solid w-250px ps-14'
              placeholder='Buscar servicio...'
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        </div>
        <div className='card-toolbar'>
          <div className='d-flex justify-content-end' data-kt-user-table-toolbar='base'>
            <button type='button' className='btn btn-primary' onClick={() => navigate('/servicios/ingresar')}>
              <i className='ki-duotone ki-plus fs-2'></i>
              Nuevo Servicio
            </button>
          </div>
        </div>
      </div>

      <div className='card-body py-4'>
        <div className='table-responsive'>
          <table className='table table-row-dashed table-striped table-hover align-middle gs-0 gy-4'>
            <thead>
              <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                <th className='min-w-50px'>Id</th>
                <th className='min-w-125px'>Nombre</th>
                <th className='min-w-100px'>Valor</th>
                <th className='min-w-125px'>Cód. Contable</th>
                <th className='min-w-100px text-center'>Estado</th>
                <th className='min-w-100px text-center'>Exonerado</th>
                <th className='min-w-100px text-center'>Enviar FE</th>
                <th className='text-end min-w-100px'>Acciones</th>
              </tr>
            </thead>
            <tbody className='text-gray-600 fw-semibold'>
              {filteredData.map((servicio, index) => {
                const isExpanded = expandedRow === index

                return (
                  <React.Fragment key={servicio.id}>
                    {/* Fila principal */}
                    <tr
                      style={{cursor: 'pointer'}}
                      onClick={() => toggleExpandRow(index)}
                    >
                      <td>{servicio.id}</td>
                      <td>
                        <div className='d-flex flex-column'>
                          <span className='text-gray-800 mb-1'>{servicio.nombre}</span>
                        </div>
                      </td>
                      <td>${parseFloat(servicio.valor).toFixed(2)}</td>
                      <td>
                        {servicio.codigo_contable ? (
                          <span className='text-gray-700 fw-semibold'>{servicio.codigo_contable}</span>
                        ) : (
                          <span className='text-muted'>—</span>
                        )}
                      </td>
                      <td className='text-center'>
                        {servicio.estado ? (
                          <span className='badge badge-light-success'>Activo</span>
                        ) : (
                          <span className='badge badge-light-danger'>Inactivo</span>
                        )}
                      </td>
                      <td className='text-center'>
                        {servicio.exonerado ? (
                          <span className='badge badge-light-info'>Sí</span>
                        ) : (
                          <span className='badge badge-light-secondary'>No</span>
                        )}
                      </td>
                      <td className='text-center'>
                        <span className={`badge ${servicio.enviar_facturacion_electronica ? 'badge-light-success' : 'badge-light-secondary'}`}>
                          {servicio.enviar_facturacion_electronica ? 'Sí' : 'No'}
                        </span>
                      </td>
                      <td className='text-end' onClick={(e) => e.stopPropagation()}>
                        <button
                          className='btn btn-light btn-active-light-primary btn-sm'
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            navigate(`/servicios/edit/${servicio.id}`, { state: { servicio } })
                          }}
                        >
                          Editar
                        </button>
                      </td>
                    </tr>

                    {/* Fila expandida con descripción */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} className='bg-light-primary'>
                          <div className='d-flex flex-column p-4'>
                            <span className='fw-bold text-gray-800 mb-2'>Descripción:</span>
                            <span className='text-gray-600 mb-3'>
                              {servicio.descripcion || 'Sin descripción disponible.'}
                            </span>
                            <div className='d-flex flex-wrap gap-2'>
                              <span className='badge badge-light-info'>
                                {servicio.variable ? 'Variable' : 'Fijo'}
                              </span>
                              <span className='badge badge-light-warning'>
                                {servicio.recurrente ? 'Recurrente' : 'Pago Único'}
                              </span>
                              {servicio.exonerado && (
                                <span className='badge badge-light-info'>Exonerado</span>
                              )}
                              {servicio.codigo_contable && (
                                <span className='badge badge-light-secondary'>
                                  Cód. Contable: {servicio.codigo_contable}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
              
              {filteredData.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                      No se encontraron registros coincidentes
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}
